'use client';

import React, { useRef, useEffect } from 'react';
import Link from 'next/link';

const industries = [
  {
    icon: '🏢',
    title: 'Commercial Real Estate',
    short: 'CRE',
    description: 'Fund EMDs for multifamily, office, industrial, and mixed-use acquisitions.',
    tags: ['Multifamily', 'Office', 'Industrial', 'Retail'],
    color: '#52B788',
  },
  {
    icon: '⛽',
    title: 'Oil, Gas & Mineral Leasing',
    short: 'Energy',
    description: 'Lock down drilling rights with a cash bonus deposit while title opinions are completed.',
    tags: ['Mineral Rights', 'Title Opinion', 'Upstream', 'Exploration'],
    color: '#E9C46A',
  },
  {
    icon: '🤝',
    title: 'Business Acquisitions',
    short: 'M&A',
    description: 'Bridge the deposit gap when your net worth is tied up in 401ks or home equity during LOI signing.',
    tags: ['Search Funds', 'LOI Deposits', 'QofE Period', '$50K–$250K'],
    color: '#C9A84C',
  },
  {
    icon: '✈️',
    title: 'Private Aviation',
    short: 'Jets',
    description: 'Ground the aircraft fast. Fund pre-purchase inspection escrow deposits on Cessna, Gulfstream, and Embraer.',
    tags: ['Pre-Buy Escrow', 'Gulfstream', 'Cessna', '$100K–$1M+'],
    color: '#74C69D',
  },
  {
    icon: '🏗️',
    title: 'Government & Infrastructure',
    short: 'Tendering',
    description: 'Win public works contracts without draining bonding capacity. Fund tender EMDs while cash is trapped in active jobs.',
    tags: ['Public Works', 'Bid Bonds', 'Construction', 'Roads & Schools'],
    color: '#A8DADC',
  },
  {
    icon: '⛵',
    title: 'Luxury Maritime',
    short: 'Yachts',
    description: 'Fund survey and sea trial escrow deposits on 80ft+ yachts — fully refundable during the inspection period.',
    tags: ['80ft+ Yachts', 'Sea Trial', 'Survey Period', 'Escrow'],
    color: '#52B788',
  },
];

const featuredIndustries = industries.slice(0, 2);
const subIndustries = industries.slice(2);

export default function IndustriesShowcase() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('[data-reveal]');
            els.forEach((el, i) => {
              const element = el as HTMLElement;
              setTimeout(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
                element.style.filter = 'blur(0)';
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="industries"
      className="py-28 px-6 relative z-10"
      style={{ background: 'var(--color-bg)' }}
      aria-label="Industries served by EarnestBridge"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-4">Industry Verticals</p>
          <h2
            className="font-display font-semibold mb-5"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              color: 'var(--color-foreground)',
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
            }}
          >
            EMD Financing Across{' '}
            <span
              className="italic"
              style={{ color: 'var(--color-primary)' }}
            >
              Every Industry
            </span>
          </h2>
          <p
            className="text-base leading-relaxed max-w-2xl mx-auto"
            style={{ color: 'var(--color-muted)' }}
          >
            Wherever a deal requires an earnest money deposit, EarnestBridge can fund it.
            We serve six major verticals — each with unique timelines, deposit structures, and due diligence periods.
          </p>
        </div>

        {/* Featured Row — CRE + Oil/Gas (larger) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {featuredIndustries.map((industry, i) => (
            <div
              key={industry.title}
              data-reveal
              className="group rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:-translate-y-1"
              style={{
                opacity: 0,
                transform: 'translateY(28px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                background: 'var(--color-surface)',
                border: `1px solid ${industry.color}30`,
                boxShadow: `0 4px 20px ${industry.color}12`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${industry.color}25`;
                (e.currentTarget as HTMLElement).style.borderColor = `${industry.color}60`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = `0 4px 20px ${industry.color}12`;
                (e.currentTarget as HTMLElement).style.borderColor = `${industry.color}30`;
              }}
            >
              {/* Featured label */}
              <div className="mb-5">
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.3em] px-2.5 py-1 rounded-full"
                  style={{ background: `${industry.color}20`, color: industry.color }}
                >
                  ★ Featured Vertical
                </span>
              </div>

              {/* Icon + badge */}
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
                  style={{ background: `${industry.color}20` }}
                >
                  {industry.icon}
                </div>
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.3em] px-2.5 py-1 rounded-full"
                  style={{
                    background: `${industry.color}15`,
                    color: industry.color,
                  }}
                >
                  {industry.short}
                </span>
              </div>

              {/* Title */}
              <h3
                className="font-display font-semibold text-2xl mb-4 leading-tight"
                style={{ color: 'var(--color-foreground)' }}
              >
                {industry.title}
              </h3>

              {/* Description */}
              <p
                className="text-base leading-relaxed mb-6"
                style={{ color: 'var(--color-muted)' }}
              >
                {industry.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {industry.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] font-semibold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full"
                    style={{
                      background: 'rgba(27,67,50,0.06)',
                      color: 'var(--color-muted)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Sub-industries Row — 4 smaller cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {subIndustries.map((industry, i) => (
            <div
              key={industry.title}
              data-reveal
              className="group rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1"
              style={{
                opacity: 0,
                transform: 'translateY(28px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                background: 'var(--color-surface)',
                border: '1px solid rgba(27,67,50,0.1)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(27,67,50,0.12)';
                (e.currentTarget as HTMLElement).style.borderColor = `${industry.color}40`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(27,67,50,0.1)';
              }}
            >
              {/* Icon + badge */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                  style={{ background: `${industry.color}18` }}
                >
                  {industry.icon}
                </div>
                <span
                  className="text-[9px] font-bold uppercase tracking-[0.3em] px-2 py-1 rounded-full"
                  style={{
                    background: `${industry.color}15`,
                    color: industry.color,
                  }}
                >
                  {industry.short}
                </span>
              </div>

              {/* Title */}
              <h3
                className="font-display font-semibold text-sm mb-2 leading-tight"
                style={{ color: 'var(--color-foreground)' }}
              >
                {industry.title}
              </h3>

              {/* Description */}
              <p
                className="text-xs leading-relaxed mb-4"
                style={{ color: 'var(--color-muted)' }}
              >
                {industry.description}
              </p>

              {/* Tags — show first 2 only for compact layout */}
              <div className="flex flex-wrap gap-1.5">
                {industry.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="text-[9px] font-semibold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full"
                    style={{
                      background: 'rgba(27,67,50,0.06)',
                      color: 'var(--color-muted)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            href="/industries"
            className="btn-primary text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full"
          >
            Explore All Industries →
          </Link>
          <Link
            href="/contact"
            className="btn-outline text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full"
          >
            Submit Your Deal
          </Link>
        </div>
      </div>
    </section>
  );
}
